/**
 * Reporting duties (AGENT-REPORTING; spec 13 §Reporting duties, founder-approved 2026-10-21):
 * the pure half. A duty says when a seat measurable's number is due — a due rule in company
 * time — and this module answers, for an instant: the next and the latest due instant, the
 * period each one covers, the duty's state (blocked / late / reported / due soon / scheduled),
 * a one-line description of the rule, and the agent's standing-order text for it.
 *
 * Everything is computed in the company's IANA timezone with Luxon, so a due time keeps its
 * wall-clock hour across daylight-saving changes. Monthly days past the month's end clamp to its
 * last day (day 31 in February is the 28th or 29th).
 */
import { DateTime } from "luxon";
import { type OrgCalendarConfig, type PeriodType, periodFromKey, periodKeyFor } from "./periods";

export type DueCovers = "current" | "previous";

export type DueRule =
  | { freq: "daily"; time: string; covers?: DueCovers | undefined }
  | { freq: "weekly"; weekday: number; time: string; covers?: DueCovers | undefined }
  | { freq: "monthly"; day: number; time: string; covers?: DueCovers | undefined };

export type ReportingMethod = "agent_tool" | "manual" | "import" | "events";

/**
 * DE-WIZARD: the due rule of the `agent_tool` duty a DE's job sets when the "Add digital
 * employee" wizard marks a KPI "reported by the agent" with a cadence. The duty editor's
 * defaults: 17:00 company time; weekly on Friday; monthly on the 1st, for the month before.
 */
export function agentJobDueRule(duty: "daily" | "weekly" | "monthly"): DueRule {
  if (duty === "daily") return { freq: "daily", time: "17:00" };
  if (duty === "monthly") return { freq: "monthly", day: 1, time: "17:00", covers: "previous" };
  return { freq: "weekly", weekday: 5, time: "17:00" };
}

/** blocked > late > reported > due_soon > scheduled (the first that applies). */
export type DutyState = "blocked" | "late" | "reported" | "due_soon" | "scheduled";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function hm(time: string): { hour: number; minute: number } {
  const m = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m) throw new Error(`not an HH:MM time: ${time}`);
  return { hour: Number(m[1]), minute: Number(m[2]) };
}

/** The rule's due instant in the slot (day, week or month) containing local `d`. */
function dueInSlot(rule: DueRule, d: DateTime): DateTime {
  const t = hm(rule.time);
  const day = d.startOf("day");
  if (rule.freq === "daily") return day.set(t);
  if (rule.freq === "weekly") return day.minus({ days: day.weekday - rule.weekday }).set(t);
  const month = day.startOf("month");
  return month.set({ day: Math.min(rule.day, month.daysInMonth ?? 28), ...t });
}

function step(rule: DueRule, d: DateTime, n: number): DateTime {
  if (rule.freq === "daily") return d.plus({ days: n });
  if (rule.freq === "weekly") return d.plus({ weeks: n });
  return d.startOf("month").plus({ months: n });
}

/** The first due instant strictly after `now`. */
export function nextDue(rule: DueRule, timezone: string, now: Date): Date {
  const local = DateTime.fromJSDate(now, { zone: timezone });
  let c = dueInSlot(rule, local);
  if (c.toMillis() <= now.getTime()) c = dueInSlot(rule, step(rule, local, 1));
  return c.toJSDate();
}

/** The latest due instant at or before `now`. */
export function lastDue(rule: DueRule, timezone: string, now: Date): Date {
  const local = DateTime.fromJSDate(now, { zone: timezone });
  let c = dueInSlot(rule, local);
  if (c.toMillis() > now.getTime()) c = dueInSlot(rule, step(rule, local, -1));
  return c.toJSDate();
}

/** The metric-cadence period a due instant covers (`covers`: the one containing it, or before). */
export function coveredPeriodKey(
  cfg: OrgCalendarConfig,
  type: PeriodType,
  rule: DueRule,
  due: Date,
): string {
  const own = periodKeyFor(cfg, type, due);
  if ((rule.covers ?? "current") === "current") return own;
  const p = periodFromKey(cfg, type, own);
  return periodKeyFor(cfg, type, new Date(p.startsAt.getTime() - 1));
}

export type DutyStatusInput = {
  rule: DueRule;
  cfg: OrgCalendarConfig;
  /** The metric's cadence period type (null: no cadence → blocked by the caller). */
  periodType: PeriodType;
  leadMinutes: number;
  /** Due instants before this one never count (the duty did not exist yet). */
  since: Date;
  /** Periods that have a live value or a value pending review. */
  reported: ReadonlySet<string>;
  blockedReason: string | null;
  now: Date;
};

export type DutyStatus = {
  state: DutyState;
  next_due_at: Date;
  next_period_key: string;
  /** The latest due instant since the duty began (null: none yet). */
  last_due_at: Date | null;
  last_period_key: string | null;
  /** The due instant whose period is still unreported (`late` only). */
  late_since: Date | null;
  blocked_reason: string | null;
};

export function dutyStatus(i: DutyStatusInput): DutyStatus {
  const next = nextDue(i.rule, i.cfg.timezone, i.now);
  const nextKey = coveredPeriodKey(i.cfg, i.periodType, i.rule, next);
  const prev = lastDue(i.rule, i.cfg.timezone, i.now);
  const counted = prev.getTime() >= i.since.getTime() ? prev : null;
  const prevKey = counted ? coveredPeriodKey(i.cfg, i.periodType, i.rule, counted) : null;
  const late = counted !== null && prevKey !== null && !i.reported.has(prevKey);
  const soon = next.getTime() - i.leadMinutes * 60_000 <= i.now.getTime();
  const state: DutyState = i.blockedReason
    ? "blocked"
    : late
      ? "late"
      : i.reported.has(nextKey)
        ? "reported"
        : soon
          ? "due_soon"
          : "scheduled";
  return {
    state,
    next_due_at: next,
    next_period_key: nextKey,
    last_due_at: counted,
    last_period_key: prevKey,
    late_since: state === "late" ? counted : null,
    blocked_reason: i.blockedReason,
  };
}

/** "Weekly, Friday 17:00" · "Monthly, day 3, 09:00" · "Daily, 17:00" (+ "(previous week)"). */
export function describeDueRule(rule: DueRule, periodWord?: string): string {
  const when =
    rule.freq === "daily"
      ? `Daily, ${rule.time}`
      : rule.freq === "weekly"
        ? `Weekly, ${WEEKDAYS[rule.weekday - 1] ?? "?"} ${rule.time}`
        : `Monthly, day ${rule.day}, ${rule.time}`;
  return (rule.covers ?? "current") === "previous" && periodWord
    ? `${when} (previous ${periodWord})`
    : when;
}

/** The deadline as the agent reads it: "Friday 17:00" / "day 3 of the month at 09:00" / "17:00". */
function deadline(rule: DueRule): string {
  if (rule.freq === "daily") return `${rule.time} every day`;
  if (rule.freq === "weekly") return `${WEEKDAYS[rule.weekday - 1] ?? "?"} ${rule.time} every week`;
  return `day ${rule.day} of each month at ${rule.time}`;
}

const PERIOD_WORD: Record<PeriodType, string> = {
  day: "day",
  week: "week",
  month: "month",
  quarter: "quarter",
  year: "year",
};

export type DutyOrderInput = {
  metricName: string;
  metricKey: string;
  method: ReportingMethod;
  rule: DueRule;
  timezone: string;
  periodType: PeriodType;
  /** How the agent reports the metric (`kpis.read` `report`). */
  report: "value" | "event" | "none";
  evidenceRequired: boolean;
};

/**
 * The agent's standing order for one duty (13 §Reporting duties: generated, never free prose):
 * the "Reporting" section of its "Your KPIs" block, and the duty editor's preview.
 */
export function dutyOrderText(i: DutyOrderInput): string {
  const name = `${i.metricName} (${i.metricKey})`;
  const which = (i.rule.covers ?? "current") === "previous" ? "previous" : "current";
  const period = `the ${which} ${PERIOD_WORD[i.periodType]}`;
  const by = `by ${deadline(i.rule)} (${i.timezone})`;
  const evidence = i.evidenceRequired
    ? " Include an evidence link (evidence.url) with every report."
    : " Say in the evidence what the number stands for.";
  if (i.method === "agent_tool") {
    if (i.report === "event")
      return `${name}: record each occurrence with outcomes.record (kind event) as it happens, so ${period} is complete ${by}.${evidence}`;
    if (i.report === "value")
      return `${name}: report ${period}'s value with outcomes.record (kind value) ${by}.${evidence}`;
    return `${name}: derived from other numbers; nothing to report.`;
  }
  if (i.method === "events")
    return `${name}: counted from events your integrations send; make sure ${period} is complete ${by}.`;
  return `${name}: entered by a person (${i.method === "import" ? "CSV import" : "manual entry"}) ${by}; you do not report it.`;
}
